import React, { PropsWithChildren, useEffect, useMemo } from 'react';

import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import { useCurrentAccountEntitlementsStore } from './stores/CurrentAccountEntitlementsStore';

export type CurrentAccount = {
  id: string;
  name: string;
  imageUrl: string;
  accessLevels: {
    accountAdministrator: boolean;
    accountStandardsAdministrator: boolean;
    accountExecutive: boolean;
  };
  accountEntitledFeatureKeys?: string[];
  userEntitledFeatureKeys?: string[];
  products: {
    /** The type of product */
    key: string;
    /** The id of the resource associated to this group */
    category: string;
    status: string;
    /** The display name of the product */
    name: string;
    /** The url for the product icon */
    iconUrl: string;
    /** Boolean indicating of the user is authorized to access this product */
    authorized: boolean;
    /** The url to redirect to if the user is not authorized to access this product */
    redirectUrl?: string;
    /** The url for the default tool in this product. Will default to remediationUrl if user is not authorized */
    defaultUrl: string;
    /** The product access levels */
    accessLevels: {
      /** Indicates the user is an administrator of the product */
      productAdministrator: boolean;
      /** Indicates the user is a member of the product */
      productMember: boolean;
    };
    /** List of tools available in the product */
    tools: {
      /** The type of tool */
      key: string;
      /** The display name of the tool */
      name: string;
      /** The url for the tool icon */
      iconUrl?: string | null;
      /** The url of the tool */
      url: string;
      /** Boolean indicating of the user is authorized to access this tool */
      authorized?: boolean;
      /** The url to redirect to if the user is not authorized to access this tool */
      redirectUrl?: string;
    }[];
  }[];
};

type CurrentAccountEntitlementsContextValue = {
  loading?: boolean;
  error?: unknown;
  retry: () => void;
  currentAccount?: CurrentAccount;
  setCurrentAccount: (nextAccount?: CurrentAccount) => void;
};

export const CurrentAccountEntitlementsContext =
  getOrCreateGlobalContext<CurrentAccountEntitlementsContextValue>(
    CONTEXT_NAMES.CurrentAccountEntitlementsContext,
  );

/**
 * Returns the current account entitlements context value. Must be used inside `CurrentAccountEntitlementsProvider`.
 */
export const useCurrentAccountEntitlements = () =>
  useStoreWithValidation<CurrentAccountEntitlementsContextValue>(
    CONTEXT_NAMES.CurrentAccountEntitlementsContext,
    {
      hook: 'useCurrentAccountEntitlements',
      provider: 'CurrentAccountEntitlementsProvider',
      context: CurrentAccountEntitlementsContext,
    },
  );

export const useCurrentAccountEntitlementsProvider = () => {
  const accountId = useCurrentAccountId();
  const url = useMemo(() => {
    if (!accountId) {
      return null;
    }

    return `${getAdminConstructionApiUrl()}/internal/v1/account-entitlements/${accountId}?includeBlockedProductsAndTools=true`;
  }, [accountId]);

  const {
    error,
    loading,
    fetchData,
    retry,
    setCurrentAccount,
    currentAccount,
  } = useCurrentAccountEntitlementsStore();

  useEffect(() => {
    if (url) {
      fetchData(url);
    }
  }, [fetchData, url]);

  useEffect(() => {
    if (
      error &&
      !((error as unknown) instanceof Error) &&
      error.status !== 401
    ) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling current account entitlements', error);
    }
  }, [error]);

  return useMemo(
    () => ({
      ...(error && { error }),
      loading,
      retry,
      currentAccount,
      setCurrentAccount,
    }),
    [error, loading, retry, currentAccount, setCurrentAccount],
  );
};

export function CurrentAccountEntitlementsProvider({
  children,
}: PropsWithChildren) {
  const currentAccountEntitlementsContextValue =
    useCurrentAccountEntitlementsProvider();

  useProviderOnlyOnce({
    context: CurrentAccountEntitlementsContext,
    contextName: CONTEXT_NAMES.CurrentAccountEntitlementsContext,
  });

  return (
    <CurrentAccountEntitlementsContext.Provider
      value={currentAccountEntitlementsContextValue}
    >
      {children}
    </CurrentAccountEntitlementsContext.Provider>
  );
}
